// Interaktív avatar (profilkép) feltöltő/módosító/törlő — a „Profilom" fejlécében. A NAGY, középre igazított
// avatarra vitt kurzornál diszkrét overlay jelenik meg (kamera-ikon) → fájlválasztó. A kép KLIENS-oldalon
// négyzetre vágódik (center-crop) + downscale-elődik (max 512px), majd `image/webp` blobként:
//   1) POST /profile/avatar/upload-url → { uploadUrl } (a típus + méret az Odin presigned-aláírásába kötve)
//   2) PUT <uploadUrl> a blobbal — a bájtok KÖZVETLENÜL az objektum-tárba mennek (NEM a BFF-en át)
//   3) POST /profile/avatar/commit → friss avatarUrl → a ['profile'] + ['session'] cache invalidálása
// A szerver a forrás-igazság (típus + méret); a kliens-oldali elő-validáció csak a jobb UX-ért van. A titok/URL
// SOHA nem logolt. Hiba → generikus toast (a jog-nevet/PII-t sosem szivárogtatjuk).
import { AVATAR_CONTENT_TYPES } from '@atlas/contracts';
import { ArrowPathIcon, CameraIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Avatar } from '@/components/ui/avatar';
import { profileKeys } from '@/features/profile/api';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/** A kliens-oldali feldolgozás felső él-mérete (px) — a kép leghosszabb oldala ekkorára downscale-elődik. */
const MAX_SIDE = 512;
/** A böngésző fájlválasztó `accept`-je (a szerver allowlistjével összhangban; a KIMENET mindig webp/png/jpeg). */
const ACCEPT = 'image/png,image/jpeg,image/webp';

type AvatarContentType = (typeof AVATAR_CONTENT_TYPES)[number];
/** A feldolgozott blob típusa az allowlist ellen (webp / PNG-fallback); nem-allowlistelt → null. */
function narrowContentType(type: string): AvatarContentType | null {
  return (AVATAR_CONTENT_TYPES as readonly string[]).includes(type)
    ? (type as AvatarContentType)
    : null;
}
/** Elő-validáció: a nyers, feldolgozás ELŐTTI fájl felső korlátja (a feldolgozott blob jóval kisebb lesz). */
const MAX_INPUT_BYTES = 15 * 1024 * 1024;

/** Egy `File`-t `HTMLImageElement`-be tölt (object URL-en át, amit utána felszabadítunk). */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image-decode-failed'));
    };
    img.src = url;
  });
}

/** Canvas → Blob (Promise-ösítve). Ha a böngésző nem tudja a kért típust, PNG-re esik vissza (allowlistelt). */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * Kliens-oldali feldolgozás: középre-vágás NÉGYZETRE (a rövidebb oldal mentén) + downscale (max `MAX_SIDE`),
 * majd export `image/webp`-be (fallback PNG, ha a böngésző nem támogatja). Nehéz cropper-lib NÉLKÜL — sima
 * canvas-műveletek. A visszatérő blob típusa a szerver allowlistjében van (png/jpeg/webp).
 */
async function processToSquare(file: File): Promise<Blob> {
  const img = await loadImage(file);
  const side = Math.min(img.naturalWidth, img.naturalHeight);
  if (side <= 0) throw new Error('image-empty');
  const sx = Math.max(0, (img.naturalWidth - side) / 2);
  const sy = Math.max(0, (img.naturalHeight - side) / 2);
  const target = Math.min(side, MAX_SIDE);

  const canvas = document.createElement('canvas');
  canvas.width = target;
  canvas.height = target;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-unavailable');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);

  const blob = await canvasToBlob(canvas, 'image/webp', 0.9);
  if (!blob) throw new Error('encode-failed');
  return blob;
}

export function AvatarUploader({
  name,
  avatarUrl,
  teamColor,
}: {
  name: string;
  /** A saját profil-GET avatarUrl-je (presigned) — null → nincs kép (monogram fallback). */
  avatarUrl: string | null;
  /** Részleg szín-token (a /session-ből) — az avatar-háttérhez, mint a navbarban. */
  teamColor: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  /** A header (profil-GET) ÉS a navbar (session) is frissül a sikeres művelet után. */
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: profileKeys.detail() });
    void queryClient.invalidateQueries({ queryKey: profileKeys.session() });
  };

  const pick = (): void => {
    if (!busy) inputRef.current?.click();
  };

  const onFile = async (file: File): Promise<void> => {
    // Elő-validáció (UX) — a szerver a forrás-igazság. Rossz típus / túl nagy nyers fájl → generikus toast.
    if (!file.type.startsWith('image/')) {
      toast.error(t('toast:profile.avatarInvalidType'));
      return;
    }
    if (file.size > MAX_INPUT_BYTES) {
      toast.error(t('toast:profile.avatarTooLarge'));
      return;
    }
    setBusy(true);
    try {
      const blob = await processToSquare(file);
      const contentType = narrowContentType(blob.type);
      if (!contentType) {
        toast.error(t('toast:profile.avatarInvalidType'));
        return;
      }
      // 1) Presigned PUT-URL (a típus + méret az aláírásba kötve).
      const urlRes = await api.profileAvatarUploadUrl.mutation({
        body: { contentType, contentLength: blob.size },
      });
      if (urlRes.status !== 200) {
        if (urlRes.status === 429) toast.error(t('toast:profile.avatarRateLimited'));
        else if (urlRes.status === 422) toast.error(t('toast:profile.avatarTooLarge'));
        else toast.error(t('toast:profile.avatarError'));
        return;
      }
      // 2) A bájtok KÖZVETLENÜL a tárba (SeaweedFS) — a Content-Type a blob típusa (az aláírással egyező); a
      //    Content-Length-et a böngésző állítja a body-ból. A BFF-et NEM érinti.
      const put = await fetch(urlRes.body.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type },
        body: blob,
      });
      if (!put.ok) {
        toast.error(t('toast:profile.avatarError'));
        return;
      }
      // 3) Commit → friss avatarUrl.
      const commit = await api.profileAvatarCommit.mutation({ body: {} });
      if (commit.status !== 200) {
        toast.error(t('toast:profile.avatarError'));
        return;
      }
      toast.success(t('toast:profile.avatarSaved'));
      invalidate();
    } catch {
      toast.error(t('toast:profile.avatarError'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = ''; // ugyanaz a fájl újraválaszthatóvá válik
    }
  };

  const onDelete = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.profileAvatarDelete.mutation({});
      if (res.status === 200) {
        toast.success(t('toast:profile.avatarRemoved'));
        invalidate();
      } else {
        toast.error(t('toast:profile.avatarError'));
      }
    } catch {
      toast.error(t('toast:profile.avatarError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2 pb-1">
      <div className="group relative">
        <Avatar
          name={name}
          src={avatarUrl}
          accentColor={teamColor}
          size="xl"
          className="size-24 text-2xl"
        />
        {/* Diszkrét overlay — csak hoverre/fókuszra látszik (kivéve amíg dolgozunk: akkor spinner). */}
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          aria-label={t('profile.avatar.edit')}
          title={t('profile.avatar.edit')}
          className={cn(
            'absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
            busy
              ? 'opacity-100'
              : 'cursor-pointer opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
          )}
        >
          {busy ? (
            <ArrowPathIcon className="size-6 animate-spin" />
          ) : (
            <CameraIcon className="size-6" />
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
        />
      </div>
      {/* Törlés-vezérlő — csak ha van feltöltött kép; diszkrét piros link (visszaállítható, nincs megerősítés). */}
      {avatarUrl && (
        <button
          type="button"
          onClick={() => void onDelete()}
          disabled={busy}
          className="text-destructive hover:text-destructive/80 focus-visible:ring-ring inline-flex items-center gap-1 rounded-sm px-1 py-0.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 disabled:opacity-50"
          title={t('profile.avatar.remove')}
        >
          <TrashIcon className="size-3.5" />
          {t('profile.avatar.remove')}
        </button>
      )}
    </div>
  );
}
